import { useEffect, useState } from "react";
import { Share, Plus, X } from "lucide-react";

/**
 * iOSInstallHint — one-time banner shown only on iPhones running Safari that
 * are NOT yet installed as a PWA. Web push on iOS requires home-screen install
 * (Apple restriction). Dismissed forever via localStorage once tapped.
 */
const KEY = "ios:install-hint:dismissed-v1";

const isIosSafariNotInstalled = () => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    if (!isIOS) return false;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (!isSafari) return false;
    // Standalone = already installed
    const standalone =
        ("standalone" in window.navigator && window.navigator.standalone) ||
        window.matchMedia?.("(display-mode: standalone)").matches;
    return !standalone;
};

export default function IOSInstallHint() {
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!isIosSafariNotInstalled()) return;
        try {
            if (localStorage.getItem(KEY) === "1") return;
        } catch { /* noop */ }
        // Defer the hint so it doesn't blink in during first paint
        const t = setTimeout(() => setShow(true), 3000);
        return () => clearTimeout(t);
    }, []);

    const dismiss = () => {
        setShow(false);
        try { localStorage.setItem(KEY, "1"); } catch { /* noop */ }
    };

    if (!show) return null;

    return (
        <div
            data-testid="ios-install-hint"
            className="fixed bottom-20 inset-x-3 z-[55] bg-white rounded-2xl shadow-2xl border border-rosegold/40 overflow-hidden animate-in slide-in-from-bottom-2"
        >
            <div className="p-4 pr-3 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-ivory-soft text-rosegold flex items-center justify-center flex-shrink-0">
                    <Plus size={20} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-heading font-semibold text-stone-900">
                        Get order updates on your phone
                    </p>
                    <p className="text-xs text-stone-600 mt-1 leading-snug">
                        Tap <Share size={11} className="inline -mt-0.5" /> below, then <strong>“Add to Home Screen”</strong>. Open JnS from your home screen once — you'll get instant order updates even when the browser is closed.
                    </p>
                </div>
                <button
                    onClick={dismiss}
                    data-testid="ios-install-hint-dismiss"
                    aria-label="Dismiss"
                    className="p-1 -mt-1 -mr-1 rounded-md text-stone-400 hover:bg-stone-100"
                >
                    <X size={16} />
                </button>
            </div>
        </div>
    );
}
